import type { EventSourcePolyfill } from 'event-source-polyfill';

type EventSourceInstance = EventSource | EventSourcePolyfill;

/**
 * EventSource connection states
 * @see https://developer.mozilla.org/en-US/docs/Web/API/EventSource/readyState
 */
enum EventSourceState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSED = 2,
}

interface PooledConnection {
  /** The URL of the EventSource connection */
  url: string;
  /** The active EventSource instance (if connection is established) */
  eventSource?: EventSourceInstance;
  /** Pending promise while connection is being established */
  connectionPromise?: Promise<EventSourceInstance>;
  /** Number of active references to this connection */
  referenceCount: number;
}

/**
 * SSEPool manages EventSource connections with reference counting.
 * It prevents duplicate connections to the same URL and automatically
 * closes connections when no longer needed.
 */
class SSEPool {
  private pool = new Map<string, PooledConnection>();

  private isConnectionAlive = (eventSource?: EventSourceInstance): boolean => {
    if (!eventSource) return false;

    return eventSource.readyState === EventSourceState.CONNECTING || eventSource.readyState === EventSourceState.OPEN;
  };

  /**
   * Gets an existing EventSource or creates a new one for the given URL.
   * Implements reference counting - multiple calls with the same URL will
   * reuse the same connection.
   */
  getOrCreate(url: string, createConnection: () => Promise<EventSourceInstance>): Promise<EventSourceInstance> {
    const existingConnection = this.pool.get(url);

    if (existingConnection) {
      if (this.isConnectionAlive(existingConnection.eventSource)) {
        existingConnection.referenceCount++;
        return Promise.resolve(existingConnection.eventSource!);
      }

      if (existingConnection.connectionPromise) {
        existingConnection.referenceCount++;
        return existingConnection.connectionPromise;
      }
    }

    // No existing connection, create a new one
    const newConnection: PooledConnection = {
      url,
      referenceCount: 1,
    };

    this.pool.set(url, newConnection);

    // Create the connection and update the record
    newConnection.connectionPromise = createConnection()
      .then((eventSource) => {
        newConnection.eventSource = eventSource;
        newConnection.connectionPromise = undefined;
        return eventSource;
      })
      .catch((error) => {
        // Remove from pool on failure to allow retry
        this.pool.delete(url);
        throw error;
      });

    return newConnection.connectionPromise;
  }

  /**
   * Releases a reference to an EventSource connection.
   * When reference count reaches 0, the connection is closed and removed.
   */
  release(url: string): void {
    const connection = this.pool.get(url);

    if (!connection) {
      return;
    }

    connection.referenceCount--;

    // Close and remove connection when no more references exist
    if (connection.referenceCount <= 0) {
      connection.eventSource?.close();
      this.pool.delete(url);
    }
  }
}

/**
 * Singleton instance for managing all SSE connections
 */
export const ssePool = new SSEPool();
